import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Radio, Slider, Spin, Alert, Tag, Progress, Collapse, message, Row, Col, Image } from 'antd';
import { 
  PlayCircleOutlined, 
  ReloadOutlined, 
  HistoryOutlined, 
  LoadingOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { SourcePanel } from '../types';
import { sourceStoriesApi } from '../services/api';

const { Text } = Typography;
const { Panel } = Collapse;

interface VideoGenerationCardProps {
  panel: SourcePanel;
  storyId: string;
  sceneId: string;
  videoForm?: 'LONG' | 'SHORT';
  onVideoGenerated?: (updatedPanel: SourcePanel) => void;
  onOpenHistory?: () => void;
  onVideoPreview?: () => void;
  isExtractingEndFrame?: boolean;
  onExtractEndFrame?: () => void;
}

type GenerationMode = 'auto' | 'single' | 'multi';
type GenerationStatus = 'IDLE' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

interface GenerationState {
  generationId?: string;
  status: GenerationStatus;
  mode?: string;
  estimatedDuration?: number;
  reasoning?: string;
  error?: string;
  startTime?: number;
  elapsedSeconds?: number;
}

const VideoGenerationCard: React.FC<VideoGenerationCardProps> = ({
  panel,
  storyId,
  sceneId,
  videoForm = 'LONG',
  onVideoGenerated,
  onOpenHistory,
  onVideoPreview,
  isExtractingEndFrame,
  onExtractEndFrame,
}) => {
  const cssAspectRatio = videoForm === 'SHORT' ? '9/16' : '16/9';
  const [generationState, setGenerationState] = useState<GenerationState>({
    status: panel.videoUrl ? 'COMPLETED' : 'IDLE'
  });
  const [selectedMode, setSelectedMode] = useState<GenerationMode>('auto');
  const [singleDuration, setSingleDuration] = useState<number>(10);
  const [isGenerating, setIsGenerating] = useState(false);

  // Check for ongoing generation when component mounts or panel changes
  useEffect(() => {
    const checkOngoingGeneration = async () => {
      try {
        const response = await sourceStoriesApi.getVideoHistory(panel.id);
        const generations = response.data || [];
        
        // Find the most recent generation
        const latestGen = generations.sort((a: any, b: any) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        // If latest generation is PENDING or PROCESSING, resume polling
        if (latestGen && (latestGen.status === 'PENDING' || latestGen.status === 'PROCESSING')) {
          const metadata = latestGen.metadata || {};
          setGenerationState({
            generationId: latestGen.id,
            status: latestGen.status as GenerationStatus,
            mode: metadata.shotMode || 'unknown',
            estimatedDuration: latestGen.duration,
            reasoning: metadata.reasoning,
            startTime: new Date(latestGen.createdAt).getTime(),
            elapsedSeconds: Math.floor((Date.now() - new Date(latestGen.createdAt).getTime()) / 1000)
          });
          setIsGenerating(true);
        }
      } catch (error) {
        console.error('Error checking ongoing generation:', error);
      }
    };

    checkOngoingGeneration();
  }, [panel.id]);

  // Polling for generation status
  useEffect(() => {
    if (!generationState.generationId || generationState.status === 'COMPLETED' || generationState.status === 'FAILED') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await sourceStoriesApi.getAiVideoGeneration(generationState.generationId!);
        const generation = response.data;

        setGenerationState(prev => ({
          ...prev,
          status: generation.status as GenerationStatus,
          elapsedSeconds: prev.startTime ? Math.floor((Date.now() - prev.startTime) / 1000) : 0
        }));

        if (generation.status === 'COMPLETED') {
          const panelsResponse = await sourceStoriesApi.getSourcePanels(storyId, sceneId);
          const updatedPanel = panelsResponse.data.find((p: any) => p.id === panel.id);
          if (updatedPanel) {
            onVideoGenerated?.(updatedPanel);
          }
          setIsGenerating(false);

        } else if (generation.status === 'FAILED') {
          const errorMsg = generation.errorMessage || 'Unknown error';
          setGenerationState(prev => ({
            ...prev,
            error: errorMsg
          }));
          setIsGenerating(false);
        }
      } catch (error: any) {
        console.error('Error polling generation status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [generationState.generationId, generationState.status, generationState.startTime, storyId, sceneId, panel.id, onVideoGenerated]);

  const handleGenerate = async (isRegenerate: boolean = false) => {
    if (!panel.firstFrameUrl) {
      message.warning('Vui lòng chọn 1 ảnh làm First Frame trước khi tạo video.');
      return;
    }

    // Request browser notification permission on first generation
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    try {
      setIsGenerating(true);

      let options: any = {};
      if (selectedMode === 'single') {
        options.forceMode = 'single';
        options.forceDuration = singleDuration;
      } else if (selectedMode === 'multi') {
        options.forceMode = 'multi';
      }

      // If regenerate, force AI to generate new prompt (ignore saved config)
      if (isRegenerate && selectedMode === 'auto') {
        options.regeneratePrompt = true;
      }

      const response = await sourceStoriesApi.generatePanelVideo(storyId, sceneId, panel.id, options);
      const result = response.data;

      setGenerationState({
        generationId: result.generationId,
        status: 'PROCESSING',
        mode: result.mode,
        estimatedDuration: result.estimatedDuration,
        reasoning: result.reasoning,
        startTime: Date.now(),
        elapsedSeconds: 0
      });

      message.success('Video generation started! Polling for status...');
    } catch (error: any) {
      console.error('Error generating video:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to start video generation');
      setGenerationState({
        status: 'FAILED',
        error: error.response?.data?.message || error.message || 'Unknown error'
      });
      setIsGenerating(false);
    }
  };

  const handleRetry = () => {
    // Reset to IDLE and trigger generation immediately with regeneratePrompt=true
    setGenerationState({ status: 'IDLE' });
    setIsGenerating(false);
    // Trigger generation after state reset
    setTimeout(() => handleGenerate(true), 100);
  };

  const handleRegenerate = () => {
    // Re-generate immediately with current settings, regeneratePrompt=true
    handleGenerate(true);
  };

  const renderIdleState = () => (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>🎬 Generate Video</Text>
          <br />
          <Text type="secondary">
            AI tự động chọn Single/Multi-shot | Audio: {panel.estimatedAudioDuration || 0}s
          </Text>
        </div>

        <Button
          type="primary"
          size="large"
          icon={<PlayCircleOutlined />}
          onClick={() => handleGenerate(false)}
          loading={isGenerating}
          block
        >
          Generate Video
        </Button>

        <Collapse ghost>
          <Panel header="Advanced Options" key="1">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Radio.Group value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)}>
                <Space direction="vertical">
                  <Radio value="auto">AI Auto-detect (default)</Radio>
                  <Radio value="single">
                    Force Single-shot
                    {selectedMode === 'single' && (
                      <div style={{ marginTop: 8, marginLeft: 24 }}>
                        <Text type="secondary">Duration: {singleDuration}s</Text>
                        <Slider
                          min={5}
                          max={15}
                          value={singleDuration}
                          onChange={setSingleDuration}
                          marks={{ 5: '5s', 10: '10s', 15: '15s' }}
                        />
                      </div>
                    )}
                  </Radio>
                  <Radio value="multi">Force Multi-shot</Radio>
                </Space>
              </Radio.Group>
            </Space>
          </Panel>
        </Collapse>
      </Space>
    </>
  );

  const renderGeneratingState = () => {
    const elapsed = generationState.elapsedSeconds || 0;
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <div style={{ marginTop: 16 }}>
            <Text strong style={{ fontSize: 16 }}>⏳ Đang tạo video...</Text>
          </div>
        </div>

        <div>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Mode: </Text>
              <Tag color="blue">{generationState.mode === 'single' ? 'Single-shot' : 'Multi-shot'}</Tag>
            </div>
            {generationState.reasoning && (
              <div>
                <Text type="secondary">Reasoning: </Text>
                <Text>{generationState.reasoning}</Text>
              </div>
            )}
            <div>
              <Text type="secondary">Dự kiến: </Text>
              <Text>~2-5 phút</Text>
            </div>
            <div>
              <Text type="secondary">Đã chạy: </Text>
              <Text strong>{minutes}m {seconds}s</Text>
            </div>
          </Space>
        </div>

        <Progress percent={50} status="active" showInfo={false} />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Bắt đầu: {generationState.startTime ? new Date(generationState.startTime).toLocaleTimeString() : '-'}
          </Text>
        </div>
      </Space>
    );
  };

  const renderCompletedState = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        message="Video đã tạo"
        description={
          <Space direction="vertical" size="small">
            <div>
              <Text type="secondary">Duration: </Text>
              <Text>{panel.videoDuration || 0}s</Text>
            </div>
          </Space>
        }
        type="success"
      />

      <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRegenerate}
          loading={isGenerating}
        >
          Re-generate
        </Button>
        <Button
          icon={<HistoryOutlined />}
          onClick={onOpenHistory}
        >
          History
        </Button>
      </Space>

      <Collapse ghost>
        <Panel header="Advanced Options" key="1">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Radio.Group value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)}>
              <Space direction="vertical">
                <Radio value="auto">AI Auto-detect (default)</Radio>
                <Radio value="single">
                  Force Single-shot
                  {selectedMode === 'single' && (
                    <div style={{ marginTop: 8, marginLeft: 24 }}>
                      <Text type="secondary">Duration: {singleDuration}s</Text>
                      <Slider
                        min={5}
                        max={15}
                        value={singleDuration}
                        onChange={setSingleDuration}
                        marks={{ 5: '5s', 10: '10s', 15: '15s' }}
                      />
                    </div>
                  )}
                </Radio>
                <Radio value="multi">Force Multi-shot</Radio>
              </Space>
            </Radio.Group>
          </Space>
        </Panel>
      </Collapse>
    </Space>
  );

  const renderFailedState = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        message="❌ Gen video thất bại"
        description={generationState.error || 'Unknown error'}
        type="error"
        showIcon
      />

      <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRetry}
        >
          Retry
        </Button>
        {panel.videoUrl && (
          <Button
            icon={<HistoryOutlined />}
            onClick={onOpenHistory}
          >
            History
          </Button>
        )}
      </Space>
    </Space>
  );

  const renderPreviewRow = () => (
    <Row gutter={24} align="middle" justify="center" style={{ marginBottom: 24 }}>
      <Col span={7}>
        <Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'center' }}>
          <Text strong>1. Input (First Frame)</Text>
          {panel.firstFrameUrl ? (
            <div style={{ 
              width: '100%', 
              maxWidth: 160, 
              aspectRatio: cssAspectRatio, 
              borderRadius: 8, 
              overflow: 'hidden',
              border: '2px solid #d3adf7'
            }}>
              <Image 
                src={panel.firstFrameUrl} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                preview={false}
              />
            </div>
          ) : (
            <div style={{ 
              width: '100%', 
              maxWidth: 160, 
              aspectRatio: cssAspectRatio, 
              background: '#f5f5f5', 
              borderRadius: 8, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #d9d9d9'
            }}>
              <PictureOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
            </div>
          )}
        </Space>
      </Col>
      
      <Col span={1} style={{ textAlign: 'center' }}>
        <SyncOutlined style={{ fontSize: 20, color: '#722ed1' }} />
      </Col>

      <Col span={7}>
        <Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'center' }}>
          <Text strong>2. Video AI</Text>
          {panel.videoUrl ? (
            <div 
              style={{ 
                width: '100%', 
                maxWidth: 160, 
                aspectRatio: cssAspectRatio, 
                background: '#000', 
                borderRadius: 8, 
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '2px solid #fff',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={onVideoPreview}
            >
              <video 
                key={panel.videoUrl}
                src={panel.videoUrl} 
                style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
              />
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.2)',
                zIndex: 2
              }}>
                <PlayCircleOutlined style={{ fontSize: 40, color: '#fff', opacity: 0.8 }} />
              </div>
            </div>
          ) : (
            <div style={{ 
              width: '100%', 
              maxWidth: 160, 
              aspectRatio: cssAspectRatio, 
              background: '#f5f5f5', 
              borderRadius: 8, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #d9d9d9'
            }}>
              {generationState.status === 'PROCESSING' ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              ) : (
                <>
                  <VideoCameraOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Chưa có video</Text>
                </>
              )}
            </div>
          )}
        </Space>
      </Col>

      <Col span={1} style={{ textAlign: 'center' }}>
        <SyncOutlined style={{ fontSize: 20, color: '#52c41a' }} />
      </Col>

      <Col span={7}>
        <Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'center' }}>
          <Text strong>3. Output (End Frame)</Text>
          {panel.videoUrl ? (
            panel.imagePath ? (
              <div style={{ 
                width: '100%',
                maxWidth: 160,
                aspectRatio: cssAspectRatio,
                borderRadius: 8,
                overflow: 'hidden',
                border: '2px solid #52c41a',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                background: '#fff',
                position: 'relative'
              }}>
                <div style={{ cursor: 'pointer', width: '100%', height: '100%' }} onClick={onExtractEndFrame}>
                  <Image src={panel.imagePath} preview={{ mask: 'Xem ảnh lớn' }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {isExtractingEndFrame && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                      <SyncOutlined spin style={{ color: '#fff', fontSize: '24px' }} />
                    </div>
                  )}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: '#52c41a', color: '#fff', fontSize: '10px', textAlign: 'center', zIndex: 5, padding: '2px 0' }}>
                    END FRAME
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ 
                width: 160,
                aspectRatio: cssAspectRatio,
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                border: '2px dashed #faad14',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                padding: '12px'
              }}>
                <Text type="warning" style={{ fontSize: '12px', textAlign: 'center', marginBottom: 12 }}>
                  Thiếu End Frame
                </Text>
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<SyncOutlined spin={isExtractingEndFrame} />} 
                  onClick={onExtractEndFrame}
                  loading={isExtractingEndFrame}
                  style={{ 
                    background: '#faad14',
                    borderColor: '#faad14'
                  }}
                >
                  Extract
                </Button>
              </div>
            )
          ) : (
            <div style={{ 
              width: '100%', 
              maxWidth: 160, 
              aspectRatio: cssAspectRatio, 
              background: '#f5f5f5', 
              borderRadius: 8, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #d9d9d9'
            }}>
              <PictureOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Cần có video trước</Text>
            </div>
          )}
        </Space>
      </Col>
    </Row>
  );

  const renderContent = () => {
    switch (generationState.status) {
      case 'IDLE':
        return renderIdleState();
      case 'PENDING':
      case 'PROCESSING':
        return renderGeneratingState();
      case 'COMPLETED':
        return renderCompletedState();
      case 'FAILED':
        return renderFailedState();
      default:
        return null;
    }
  };

  return (
    <Card
      title={
        <Space>
          {generationState.status === 'PROCESSING' && <LoadingOutlined />}
          {generationState.status === 'COMPLETED' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
          {generationState.status === 'FAILED' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          <VideoCameraOutlined />
          <Text>Step 2: Video Generation</Text>
          {panel.videoUrl && <Tag color="purple">Completed</Tag>}
        </Space>
      }
      size="small"
      style={{ border: '1px solid #d3adf7', background: '#f9f0ff', marginTop: 16 }}
    >
      <div style={{ padding: '16px 0' }}>
        {renderPreviewRow()}
        
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <Card size="small" style={{ width: '100%', maxWidth: 600, margin: '0 auto', textAlign: 'center', borderRadius: 8 }}>
            {renderContent()}
          </Card>
        </div>

        {!panel.firstFrameUrl && (
          <Alert
            message="* Cần chọn 1 ảnh làm First Frame trong danh sách trên trước khi tạo video."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    </Card>
  );
};

export default VideoGenerationCard;
